import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const COLORS = [
  { name: "RED", hex: "#FF003C" },
  { name: "CYAN", hex: "#00F0FF" },
  { name: "LIME", hex: "#39FF14" },
  { name: "YELLOW", hex: "#EFFF04" },
  { name: "PURPLE", hex: "#a855f7" },
];
const DURATION = 45;

function makeRound() {
  const word = COLORS[Math.floor(Math.random() * COLORS.length)];
  const ink = COLORS[Math.floor(Math.random() * COLORS.length)];
  return { word, ink };
}

export function StroopTest({ onScore }) {
  const [round, setRound] = useState(makeRound);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(DURATION);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [flash, setFlash] = useState(null);
  const reported = useRef(false);

  useEffect(() => {
    if (!running) return;
    if (time <= 0) { setRunning(false); setOver(true); return; }
    const t = setTimeout(() => setTime((x) => x - 1), 1000);
    return () => clearTimeout(t);
  }, [running, time]);

  const start = () => { setScore(0); setTime(DURATION); setRunning(true); setOver(false); setRound(makeRound()); reported.current = false; };

  const answer = (hex) => {
    if (!running) return;
    if (hex === round.ink.hex) { setScore((s) => s + 1); setFlash("ok"); }
    else { setScore((s) => Math.max(0, s - 1)); setFlash("no"); }
    setTimeout(() => setFlash(null), 150);
    setRound(makeRound());
  };

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score * 40); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center w-full max-w-md" data-testid="stroop-test">
      <div className="flex items-center justify-between w-full mb-5 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="stroop-score">SCORE: {score}</span>
        <span className={`text-lg ${time <= 10 ? "text-neon-pink animate-blink" : "text-neon-yellow"}`}>⏱ {time}s</span>
      </div>

      {!running ? (
        <div className="text-center py-10">
          {over && <p className="font-display font-black text-2xl text-neon-lime uppercase mb-2" data-testid="stroop-result">Time! Score {score}</p>}
          <p className="text-zinc-400 mb-4 max-w-xs mx-auto">Tap the button matching the COLOR of the text — not what it says!</p>
          <Button onClick={start} data-testid="stroop-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase h-12 px-8">
            <Play className="w-4 h-4 mr-2 fill-black" /> {over ? "Play Again" : "Start"}
          </Button>
        </div>
      ) : (
        <>
          <div className={`w-full text-center py-12 rounded-xl border-2 mb-6 transition-colors ${
            flash === "ok" ? "border-neon-lime" : flash === "no" ? "border-neon-pink" : "border-zinc-700"} bg-zinc-900`}>
            <span className="font-display font-black text-5xl uppercase" style={{ color: round.ink.hex }}>{round.word.name}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 w-full">
            {COLORS.map((c) => (
              <button key={c.name} onClick={() => answer(c.hex)} data-testid={`stroop-${c.name.toLowerCase()}`}
                className="py-4 rounded-lg font-bold uppercase text-xs text-black transition-transform active:scale-95"
                style={{ background: c.hex }}>
                {c.name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
